import { useEffect, useRef, useState } from "react";
import { Send, Mail, Phone, Calendar, ArrowUpRight, MessageCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  const THROTTLE_MS = 30000; // 30 second cooldown between submissions

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmitTime < THROTTLE_MS) {
      const remainingSeconds = Math.ceil((THROTTLE_MS - (now - lastSubmitTime)) / 1000);
      toast({
        title: "Please wait",
        description: `You can send another message in ${remainingSeconds} seconds.`,
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const message = formData.get("message") as string;
    const honeypot = formData.get("website") as string; // Honeypot field

    try {
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: { name, email, message, honeypot },
      });

      if (error) throw error;

      setLastSubmitTime(Date.now());
      toast({
        title: "Message sent!",
        description: "Thanks for reaching out. I'll get back to you within 24 hours.",
      });
      (e.target as HTMLFormElement).reset();
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast({
        title: "Error sending message",
        description: "Please try again or email me directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <section id="contact" ref={sectionRef} className={`${isMobile ? "py-8" : "py-16 md:py-24"} relative overflow-hidden`}>
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card to-background" />
      <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent" />
      <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-accent/20 to-transparent" />

      <div className={`container mx-auto ${isMobile ? "px-3" : "px-4 md:px-6"} relative`}>
        <div className={`grid lg:grid-cols-2 ${isMobile ? "gap-4" : "gap-8 lg:gap-16"} items-start`}>
          {/* Left Side - Info */}
          <div>
            <p
              className={`text-primary font-medium tracking-widest uppercase ${isMobile ? "mb-2 text-xs" : "mb-4"} transition-all duration-700 ${
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              }`}
            >
              Get in Touch
            </p>
            <h2
              className={`${isMobile ? "text-2xl mb-4" : "text-4xl md:text-5xl lg:text-6xl mb-8"} font-black transition-all duration-700 delay-100 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              Let's Create
              <br />
              <span className="text-gradient-static">Something Great</span>
            </h2>
            <p
              className={`${isMobile ? "text-sm mb-4" : "text-lg mb-8"} text-muted-foreground transition-all duration-700 delay-200 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              Ready to bring your vision to life? Book a free consultation.
            </p>

            {/* Contact Methods */}
            <div className={`${isMobile ? "space-y-2 mb-4" : "space-y-3 mb-8"}`}>
              {/* Phone */}
              <a
                href="tel:+16317070672"
                className={`group flex items-center gap-2 ${isMobile ? "p-2" : "p-3 md:p-4"} bg-card border border-border rounded-lg transition-all duration-500 hover:border-primary hover:shadow-[0_0_20px_hsl(var(--primary)/0.2)] ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: "300ms" }}
              >
                <div className={`${isMobile ? "w-8 h-8" : "w-10 h-10 md:w-12 md:h-12"} bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0`}>
                  <Phone size={isMobile ? 14 : 20} className="text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`${isMobile ? "text-[10px]" : "text-xs md:text-sm"} text-muted-foreground`}>Call or Text</p>
                  <p className={`${isMobile ? "text-sm" : "text-base md:text-xl"} font-bold group-hover:text-primary transition-colors truncate`}>(631) 707-0672</p>
                </div>
                <ArrowUpRight
                  size={isMobile ? 14 : 18}
                  className="flex-shrink-0 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-primary"
                />
              </a>

              {/* WhatsApp */}
              <a
                href="https://wa.me/16317070672"
                target="_blank"
                rel="noopener noreferrer"
                className={`group flex items-center gap-2 ${isMobile ? "p-2" : "p-3 md:p-4"} bg-card border border-border rounded-lg transition-all duration-500 hover:border-green-500 hover:shadow-[0_0_20px_rgba(34,197,94,0.2)] ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: "400ms" }}
              >
                <div className={`${isMobile ? "w-8 h-8" : "w-10 h-10 md:w-12 md:h-12"} bg-green-500/10 rounded-full flex items-center justify-center flex-shrink-0`}>
                  <MessageCircle size={isMobile ? 14 : 20} className="text-green-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`${isMobile ? "text-[10px]" : "text-xs md:text-sm"} text-muted-foreground`}>WhatsApp</p>
                  <p className={`${isMobile ? "text-sm" : "text-base md:text-xl"} font-bold group-hover:text-green-500 transition-colors truncate`}>(631) 707-0672</p>
                </div>
                <ArrowUpRight
                  size={isMobile ? 14 : 18}
                  className="flex-shrink-0 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-green-500"
                />
              </a>

              {/* Email */}
              <a
                href="mailto:aj.grim94@gmail.com?subject=I'd%20Like%20To%20Get%20Started%20on%20my%20Own%20Personal%20Website"
                className={`group flex items-center gap-2 ${isMobile ? "p-2" : "p-3 md:p-4"} bg-card border border-border rounded-lg transition-all duration-500 hover:border-primary hover:shadow-[0_0_20px_hsl(var(--primary)/0.2)] ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: "500ms" }}
              >
                <div className={`${isMobile ? "w-8 h-8" : "w-10 h-10 md:w-12 md:h-12"} bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0`}>
                  <Mail size={isMobile ? 14 : 20} className="text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`${isMobile ? "text-[10px]" : "text-xs md:text-sm"} text-muted-foreground`}>Email Me</p>
                  <p className={`${isMobile ? "text-xs" : "text-sm md:text-xl"} font-bold group-hover:text-primary transition-colors truncate`}>aj.grim94@gmail.com</p>
                </div>
                <ArrowUpRight
                  size={isMobile ? 14 : 18}
                  className="flex-shrink-0 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-primary"
                />
              </a>
            </div>

            {/* Consultation CTA */}
            <div
              className={`${isMobile ? "p-3" : "p-5"} bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border-2 border-primary/30 rounded-xl transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: "500ms" }}
            >
              <div className={`flex items-center ${isMobile ? "gap-2 mb-2" : "gap-3 mb-3"}`}>
                <Calendar size={isMobile ? 18 : 24} className="text-primary" />
                <h3 className={`${isMobile ? "text-sm" : "text-lg"} font-bold`}>Free Consultation</h3>
              </div>
              <p className={`${isMobile ? "text-[10px]" : "text-sm"} text-muted-foreground ${isMobile ? "mb-1" : "mb-3"}`}>
                Let's discuss your project. No pressure, just answers.
              </p>
              {!isMobile && (
                <p className="text-xs text-muted-foreground">
                  Prefer to chat? Fill out the form and I'll reach out!
                </p>
              )}
            </div>
          </div>

          {/* Right Side - Form */}
          <div
            className={`transition-all duration-1000 delay-300 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"
            }`}
          >
            <form onSubmit={handleSubmit} className={`${isMobile ? "space-y-3" : "space-y-6"}`}>
              {/* Honeypot field - hidden from users, catches bots */}
              <div className="absolute -left-[9999px]" aria-hidden="true">
                <Input
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <div className={`grid sm:grid-cols-2 ${isMobile ? "gap-3" : "gap-6"}`}>
                <div>
                  <label className={`block ${isMobile ? "text-[10px] mb-1.5" : "text-sm mb-3"} font-medium tracking-wider uppercase text-muted-foreground`}>
                    Name
                  </label>
                  <Input
                    name="name"
                    required
                    className={`${isMobile ? "h-10" : "h-14"} bg-card border-border focus:border-primary transition-colors`}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className={`block ${isMobile ? "text-[10px] mb-1.5" : "text-sm mb-3"} font-medium tracking-wider uppercase text-muted-foreground`}>
                    Email
                  </label>
                  <Input
                    name="email"
                    type="email"
                    required
                    className={`${isMobile ? "h-10" : "h-14"} bg-card border-border focus:border-primary transition-colors`}
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div>
                <label className={`block ${isMobile ? "text-[10px] mb-1.5" : "text-sm mb-3"} font-medium tracking-wider uppercase text-muted-foreground`}>
                  Message
                </label>
                <Textarea
                  name="message"
                  required
                  rows={isMobile ? 4 : 6}
                  className="bg-card border-border focus:border-primary transition-colors resize-none"
                  placeholder="Tell me about your project..."
                />
              </div>
              <Button
                type="submit"
                size={isMobile ? "default" : "lg"}
                className={`w-full ${isMobile ? "h-10 text-sm" : "h-14 text-lg"} font-bold tracking-wider uppercase transition-all duration-300 hover:shadow-[0_0_40px_hsl(var(--primary)/0.4)] group`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message
                    <Send
                      size={isMobile ? 16 : 20}
                      className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </>
                )}
              </Button>
            </form>

            {/* Read More Link */}
            <div
              className={`text-center ${isMobile ? "mt-4" : "mt-6"} transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: "700ms" }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-primary font-semibold hover:underline transition-all duration-300 group"
              >
                <span className={isMobile ? "text-sm" : "text-base"}>Visit Full Contact Page</span>
                <ArrowRight size={isMobile ? 16 : 18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;